import _ from 'lodash';
import Vue from 'vue';

import store from '@/store';
import NestedSet from '@/nested-set-lib';

import MenuApi from '@/store/api/menu';
import PageApi from '@/store/api/page';

// initial state
const state = {
    hideMenuOnLandingpageTimeout: false,
    visibleMenuOnLandingpage: false,
    page: {}
};

// getters
const getters = {
    sections: state => {
        let sections = _.get(state, 'page.sections') || [];
        return _.cloneDeep(sections).sort((a, b) => _.get(a, 'position', 0) - _.get(b, 'position', 0));
    },
    view: state => state.page.view,
    comments: state => state.page.comments,
    id: state => state.page.id,
    reference: state => state.page.reference,
    mode: state => {
        let mode = 'light';

        const view = state.page.view;
        switch (view) {
            case 'DarkContentPage':
                mode = 'dark';
                break;
        }

        return mode;
    },
    logo: state => {
        const src = require('@/assets/rundholz-logo.svg');
        let logo = {
            font: 'conduit',
            // html: '<h1 class="is-uppercase">Rundholz</h1>',
            html: `<img class="rundholz-logo" src="${src}" />`,
            component: 'div'
        };

        const menus = store.getters['menus'] || [];
        const menu = menus.find(menu => menu.key === 'main-menu');

        if (menu) {
            const collection = _.cloneDeep(menu.menuItems);
            const item = store.getters['currentPage/menuItem'];

            if (item) {
                const ancestors = NestedSet.getAncestors(collection, item) || [];

                const subtitle = store.getters['currentPage/subtitle'];
                const mode = store.getters['currentPage/mode'];

                const blackLabel =
                    ancestors.find(item => item.title.toLowerCase() === 'black label') ||
                    item.title.toLowerCase() === 'black label';

                if (blackLabel) {
                    const src = require(`@/assets/blacklabel-logo-${mode}.svg`);
                    logo.font = 'trixie';
                    logo.html = `<img class="black-label-logo" src="${src}" />`;
                    if (subtitle) logo.html += `<h2>${subtitle}</h2>`;
                } else {
                    if (subtitle) logo.html += `<h2 class="is-uppercase">${subtitle}</h2>`;
                }
            }
        }

        return logo;
    },
    subtitle: state => {
        let subtitle;

        const sections = state.page.sections || [];
        const section = sections.find(section => section.key === 'pagetitle') || {};

        const contents = section.contents || [];
        const content = contents.find(content => content.key === 'content');

        if (content && content.title) subtitle = content.title;

        return subtitle;
    },
    font: state => {
        let font = 'conduit';
        const logo = store.getters['currentPage/logo'];
        if (logo && logo.font) font = logo.font;
        return font;
    },
    visibleMenuOnLandingpage: state => state.visibleMenuOnLandingpage,
    menuItem: state => {
        let item;
        const menus = store.getters['menus'] || [];
        const menu = menus.find(menu => menu.key === 'main-menu');
        const items = _.get(menu, 'menuItems') || [];
        item = items.find(item => item.page && item.page.id === state.page.id);
        return item;
    },
    currentLevelItems: state => {
        let items = [];
        const parent = store.getters['currentPage/parent'];
        const menus = store.getters['menus'] || [];
        const menu = menus.find(menu => menu.key === 'main-menu');
        if (parent) {
            const collection = _.cloneDeep(menu.menuItems);
            items = NestedSet.getChildren(collection, parent);
        } else {
            items = menu.menuItems;
        }
        return items;
    },
    currentLevelChildren: state => {
        let items = [];
        const menus = store.getters['menus'] || [];
        const menu = menus.find(menu => menu.key === 'main-menu');
        const item = store.getters['currentPage/menuItem'];
        if (item) {
            const collection = _.cloneDeep(menu.menuItems);
            items = NestedSet.getChildren(collection, item);
        }
        return items;
    },
    parent: state => {
        let parent;
        const menus = store.getters['menus'] || [];
        const menu = menus.find(menu => menu.key === 'main-menu');
        const item = store.getters['currentPage/menuItem'];
        if (item) {
            const collection = _.cloneDeep(menu.menuItems);
            const ancestors = NestedSet.getAncestors(collection, item) || [];
            if (ancestors.length) {
                parent = ancestors[0];
            }
        }
        return parent;
    },
    depth: state => {
        let depth = 0;
        const menus = store.getters['menus'] || [];
        const menu = menus.find(menu => menu.key === 'main-menu');
        const item = store.getters['currentPage/menuItem'];
        if (item) {
            const collection = _.cloneDeep(menu.menuItems);
            if (collection && collection.length) {
                const ancestors = NestedSet.getAncestors(collection, item) || [];
                depth = ancestors.length;
            }
        }
        return depth;
    }
};

// mutations
const mutations = {
    addPageToState(state, page) {
        state.page = page;
    },
    updateCategoryPostTeasersLoading(state, { s, value }) {
        Vue.set(state.page.sections[s].teasers, 'loading', value);
    },
    insertCategoryPostTeasers(state, { s, teasers }) {
        Vue.set(state.page.sections[s], 'teasers', teasers);
    },
    visibleMenuOnLandingpage(state, value) {
        state.visibleMenuOnLandingpage = value;
    },
    setHideMenuOnLandingpageTimeout(state, timeout) {
        state.hideMenuOnLandingpageTimeout = timeout;
    },
    clearHideMenuOnLandingpageTimeout(state) {
        if (state.hideMenuOnLandingpageTimeout) clearTimeout(state.hideMenuOnLandingpageTimeout);
    },
    addBookingToEventSectionSlot(state, { key, cID, eID, sID, booking }) {
        const sec_i = _.findIndex(state.page.sections, { key });
        if (sec_i < 0) return;

        const section = state.page.sections[sec_i];
        const cat_i = _.findIndex(section.eventCategories, { id: cID });
        if (cat_i < 0) return;

        const category = section.eventCategories[cat_i];
        const evnt_i = _.findIndex(category.events, { id: eID });
        if (evnt_i < 0) return;

        const event = category.events[evnt_i];
        const slt_i = _.findIndex(event.slots, { id: sID });
        if (slt_i < 0) return;

        const i = _.get(
            state.page.sections[sec_i].eventCategories[cat_i].events[evnt_i].slots[slt_i],
            'bookings.length',
            0
        );
        Vue.set(state.page.sections[sec_i].eventCategories[cat_i].events[evnt_i].slots[slt_i].bookings, i, booking);
    }
};

// actions
const actions = {
    refreshMenuItem({ commit }, { path, cms }) {
        return MenuApi.fetchItem(path, cms).then(data => commit('addPageToState', data.page));
    },
    fetchMenuItem({ commit }, { path, cms }) {
        return MenuApi.fetchItem(path, cms);
        // .then(data => commit('addPageToState', data.page))
    },
    fetchCategoryPostTeasers({ commit }, payload) {
        return PageApi.fetchPagesPaginated(payload);
    },
    fetchCategoryPost({ commit }, payload) {
        return PageApi.fetchPage(payload);
    },
    showMenuOnLandingpage({ commit }) {
        commit('clearHideMenuOnLandingpageTimeout');
        commit('visibleMenuOnLandingpage', true);
    },
    hideMenuOnLandingpage({ commit }) {
        commit('clearHideMenuOnLandingpageTimeout');
        const timeout = setTimeout(() => {
            commit('visibleMenuOnLandingpage', false);
        }, 2000);
        commit('setHideMenuOnLandingpageTimeout', timeout);
    }
};

export default {
    namespaced: true,

    state,
    getters,
    actions,
    mutations
};
